import { Button, ButtonType, Form, FormGroup, Input, InputType, Select, SubmitButton } from '@digicert/dcone-common-ui'
import { EMAIL_REGEXP, NAME_REGEXP, RouterConfig, USER_NAME_REGEXP, USER_NAME_WITH_SPACE_REGEXP } from 'core/utils/constants'
import React, { ChangeEvent, useEffect, useState } from 'react'
import { Translate } from 'react-localize-redux'
import { mapOptionsCommonUi, translate } from 'shared/helpers/utils'
import styles from './UpdateUser.module.scss'
import { IAppState } from 'core/store/store'
import { checkIfUsernameExists } from 'public-pages/self-service-user/SelfServiceUser.actions'
import { connect } from 'react-redux'
import { IAccessScope } from 'reducers/Reducers.interfaces'
import { fetchUserById } from 'core/actions/Users.actions'
import { fetchTenantById } from 'secure-pages/tenants/Tenants.actions'
import { useHistory } from 'react-router-dom'
import { Source } from 'secure-pages/groups/Groups.types'

const UpdateUserDetails = ({ user, onCancel, onEdit, checkIfUsernameExists, profile, localeList, fetchUserById, fetchTenantById }) => {
  const [userNameExists, setUserNameExists] = useState(false)
  const [userData, setUserData] = useState(user);
  const [phoneNumber, setPhoneNumber] = useState(user.phone || '')
  const [userNameRegex, setUserNameRegex] = useState(user.user_name.includes(" ") ? USER_NAME_WITH_SPACE_REGEXP : USER_NAME_REGEXP)
  const [samlNameExists, setSamlNameExists] = useState(false)
  const [oidcNameExists, setOidcNameExists] = useState(false)
  const [isLoading, setLoading] = useState(false)
  const [signInMethods, setSignInMethods] = useState<any>([]);
  const history = useHistory()

  useEffect(() => {
    if(history.location.pathname === RouterConfig.administrators.table) { //call the user api only from the user list page
      fetchUserById(user.id).then((response: any) => {
        if (response.user_name.includes(" ")) {
          setUserNameRegex(USER_NAME_WITH_SPACE_REGEXP)
        }
        setUserData({ ...response })
      })
    }
  }, [user])

  useEffect(() => {
    const isAccountUser = profile.groups.filter(group => group.source === 'PLATFORM').every(group => group.name === 'ol_end_user');
    if(!isAccountUser) {
    fetchTenantById(user?.primary_account_id).then((acc) => {
      setSignInMethods(acc?.sign_in_methods)
    })
    }
  }, [])

  useEffect(() => {
    if (user) {
      setUserData({ ...user })
      setPhoneNumber(user.phone || '')
      setUserNameExists(false)
      setSamlNameExists(false)
      setOidcNameExists(false)
    }
  }, [onCancel])

  const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setUserData({ ...userData, [event.target.name]: event.target.value })
  }

  const onPhoneChange = (value) => {
    const phoneNo = value || ''
    setPhoneNumber(phoneNo)
    setUserData({ ...userData, 'phone': phoneNo })
  }

  const updateUserRequest = (event) => {
    event.preventDefault()
    setLoading(true)
    onEdit(event, user.groups.map(group => group.id), userData, setLoading)
  }

  const onUserNameBlur = (event: ChangeEvent<HTMLInputElement>) => {
    onInputChange(event)
    checkIfUsernameExists(event.target.value, '', 'userName', userData.id, user.access_scope.toUpperCase()).then((response: boolean) => {
      setUserNameExists(response)
    })
  }

  const validateUserName = (): any => {
    if (userNameExists) {
      return translate('notifications.exceptions.USERNAME_EXISTS') as string;
    }
  }

  const onLangChange = (lang: any): void => {
    setUserData({ ...userData, 'locale': lang })
  }

  const isUserLocked = userData.status === 'LOCKED'
  const isSCIMUser = userData.source === Source.SCIM

  const validateLangSelect = (): any => !userData?.locale ? translate('common.form.validation.required') as string : ''

  const handleKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key !== 'Enter' || e.target instanceof HTMLButtonElement) return
    e.preventDefault()
    e.stopPropagation()
  }

  return (
    <section className={styles.updateUser}>
      <Form center={false} onSubmit={(event) => { updateUserRequest(event) }} onKeyDown={handleKeyDown} className={styles.form}>
        <div>
          <FormGroup>
            <div className={styles.nameGroup}>
              <Input
                name='first_name'
                label={translate('common.form.firstName') as string}
                required
                minLength={1}
                maxLength={256}
                value={userData.first_name}
                onChange={onInputChange}
                pattern={NAME_REGEXP}
                errors={{
                  valueMissing: translate('common.form.validation.required') as string,
                  patternMismatch: translate('notifications.errors.invalidAccountNameError') as string
                }}
                id='first_name'
                disabled={isUserLocked || isSCIMUser}
              />

              <Input
                name='last_name'
                label={translate('common.form.lastName') as string}
                required
                minLength={1}
                maxLength={256}
                value={userData.last_name}
                pattern={NAME_REGEXP}
                onChange={onInputChange}
                id='last_name'
                errors={{
                  valueMissing: translate('common.form.validation.required') as string
                }}
                disabled={isUserLocked || isSCIMUser}
              />
            </div>
          </FormGroup>

          <FormGroup>
            <Input
              type={InputType.EMAIL}
              name='email'
              label={<Translate id='common.form.emailAddress' />}
              required
              value={userData.email}
              onChange={onInputChange}
              pattern={EMAIL_REGEXP}
              errors={{
                typeMismatch: translate('notifications.errors.invalidEmailError') as string,
                valueMissing: translate('common.form.validation.required') as string,
                patternMismatch: translate('notifications.errors.invalidEmailError') as string
              }}
              disabled={isSCIMUser}
            />
          </FormGroup>

          <FormGroup>
            <Input
              name='user_name'
              label={<Translate id='common.form.username' />}
              required
              value={userData.user_name}
              pattern={userNameRegex}
              errors={{
                valueMissing: translate('common.form.validation.required') as string,
                patternMismatch: translate('common.form.validation.invalidUserName') as string
              }}
              onChange={onInputChange}
              validate={validateUserName}
              onBlur={(event)=> onUserNameBlur(event)}
              disabled={isUserLocked || isSCIMUser}
            />
          </FormGroup>

          { signInMethods?.find(method=>method?.signInMethod === 'singleSignOnSaml' && method?.status === 'enabled') && (
            <FormGroup>
              <Input
                name='saml_username'
                label={<Translate id='common.form.samlUsername' />}
                required
                value={userData.saml_username}
                onChange={onInputChange}
                pattern={USER_NAME_REGEXP}
                disabled={isUserLocked}
                errors={{
                  valueMissing: translate('common.form.validation.required') as string,
                  patternMismatch: translate('common.form.validation.invalidUserName') as string
                }}
              />
            </FormGroup>
          )}
          { signInMethods?.find(method=>method?.signInMethod === 'singleSignOnOidc' && method?.status === 'enabled') && (
            <FormGroup>
              <Input
                name='oidc_username'
                label={<Translate id='common.form.oidcUsername' />}
                required
                value={userData.oidc_username}
                pattern={USER_NAME_REGEXP}
                disabled={isUserLocked}
                errors={{
                  valueMissing: translate('common.form.validation.required') as string,
                  patternMismatch: translate('common.form.validation.invalidUserName') as string
                }}
                onChange={onInputChange}
              />
            </FormGroup>
          )}

          <FormGroup>
            <Input
              label={translate('common.form.phoneOptional') as string}
              type={InputType.TELEPHONE}
              validateChanges
              value={phoneNumber}
              errors={{ valueMissing: translate('common.form.validation.required') as string }}
              onChange={onPhoneChange}
              disabled={isUserLocked}
            />
          </FormGroup>

          {user.other_phone && <FormGroup>
            <Input
              label={<Translate id='common.form.otherPhoneOptional' />}
              value={userData.other_phone}
              onChange={onInputChange}
              name='other_phone'
              disabled={isUserLocked}
            />
          </FormGroup>}
          <FormGroup>
            <Select
              getPopupContainer={(triggerNode) => triggerNode.parentElement}
              allowClear
              defaultValue={userData.locale}
              value={userData.locale}
              label={translate('profile.defaultLanguage') as string}
              onChange={onLangChange}
              options={mapOptionsCommonUi(localeList?.locales)}
              errors={{ valueMissing: translate('common.form.validation.required') as string }}
              validate={validateLangSelect}
              optionFilterProp="label"
              showSearch
              disabled={isUserLocked}
            />
          </FormGroup>
        </div>

        <div className={styles.formActions}>
          <Button buttonType={ButtonType.SECONDARY} onClick={onCancel}>
            <Translate id='common.form.buttons.cancelBtn' />
          </Button>

          <SubmitButton disabled={isLoading}>
            {translate('common.form.buttons.updateUserBtn') as string}
          </SubmitButton>
        </div>
      </Form>
    </section>
  )
}

export default connect((state: IAppState) => ({
  profile: state.profile.data,
  localeList: state.locales,
}),
  { checkIfUsernameExists,
    fetchUserById,
    fetchTenantById
   }
)(UpdateUserDetails)
